import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  FileUpload as FileUploadIcon,
  Description as ClaimsIcon,
  People as ClientsIcon,
  Security as InsurersIcon,
  Badge as InsuranceRepIcon,
  Note as NotesIcon,
  AttachFile as AttachmentsIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/axios';

interface ImportResult {
  entity: string;
  success: boolean;
  message: string;
  imported?: number;
  errors?: number;
}

const ImportPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File | null }>({});
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);

  const importEntities = [
    {
      key: 'claims',
      name: 'Claims',
      icon: <ClaimsIcon />,
      description: 'Import claims data from CSV file',
      acceptedTypes: '.csv',
    },
    {
      key: 'clients',
      name: 'Clients',
      icon: <ClientsIcon />,
      description: 'Import client information from CSV file',
      acceptedTypes: '.csv',
    },
    {
      key: 'insurers',
      name: 'Insurers',
      icon: <InsurersIcon />,
      description: 'Import insurer data from CSV file',
      acceptedTypes: '.csv',
    },
    {
      key: 'insurance-representatives',
      name: 'Insurance Representatives',
      icon: <InsuranceRepIcon />,
      description: 'Import insurance representative data from CSV file',
      acceptedTypes: '.csv',
    },
    {
      key: 'notes',
      name: 'Notes',
      icon: <NotesIcon />,
      description: 'Import notes data from CSV file',
      acceptedTypes: '.csv',
    },
    {
      key: 'attachments',
      name: 'Attachments',
      icon: <AttachmentsIcon />,
      description: 'Import attachment metadata from CSV file',
      acceptedTypes: '.csv',
    },
  ];

  const handleFileSelect = (entityKey: string, file: File | null) => {
    setSelectedFiles(prev => ({
      ...prev,
      [entityKey]: file
    }));
  };

  const handleImport = async () => {
    setImporting(true);
    setResults([]);
    
    const newResults: ImportResult[] = [];

    for (const entity of importEntities) {
      const file = selectedFiles[entity.key];
      if (!file) {
        newResults.push({
          entity: entity.name,
          success: false,
          message: 'No file selected'
        });
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/${entity.key}/import`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        newResults.push({
          entity: entity.name,
          success: true,
          message: response.data.message || 'Import successful',
          imported: response.data.imported,
          errors: response.data.errors
        });
      } catch (error: any) {
        newResults.push({
          entity: entity.name,
          success: false,
          message: error.response?.data?.message || 'Import failed'
        });
      }
    }

    setResults(newResults);
    setImporting(false);
  };

  const hasSelectedFiles = Object.values(selectedFiles).some(file => file !== null);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Import
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Upload CSV files to import data into the system. Make sure your CSV files follow the required format.
          You can download sample CSV templates from the Export page.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Files to Import
              </Typography>
              
              <List>
                {importEntities.map((entity, index) => (
                  <React.Fragment key={entity.key}>
                    <ListItem>
                      <ListItemIcon>
                        {entity.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={entity.name}
                        secondary={entity.description}
                      />
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<FileUploadIcon />}
                        disabled={importing}
                      >
                        {selectedFiles[entity.key] ? selectedFiles[entity.key]?.name : 'Choose File'}
                        <input
                          type="file"
                          hidden
                          accept={entity.acceptedTypes}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            handleFileSelect(entity.key, file);
                          }}
                        />
                      </Button>
                    </ListItem>
                    {index < importEntities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={!hasSelectedFiles || importing}
                  startIcon={importing ? <CircularProgress size={20} /> : <FileUploadIcon />}
                >
                  {importing ? 'Importing...' : 'Start Import'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Import Results
              </Typography>
              
              {results.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No import results yet. Select files and click "Start Import" to begin.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {results.map((result, index) => (
                    <Paper key={index} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {result.success ? (
                          <SuccessIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                        <Typography variant="subtitle2">
                          {result.entity}
                        </Typography>
                        {result.success && result.imported !== undefined && (
                          <Chip 
                            label={`${result.imported} imported`} 
                            size="small" 
                            color="success" 
                          />
                        )}
                        {result.errors !== undefined && result.errors > 0 && (
                          <Chip 
                            label={`${result.errors} errors`} 
                            size="small" 
                            color="error" 
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {result.message}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ImportPage; 